import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createCoDriver,
  createDriver,
  createFoRoute,
  createVehicle,
  deleteCoDriver,
  deleteDriver,
  deleteFoRoute,
  deleteVehicle,
  getCoDrivers,
  getDrivers,
  getFoRoutes,
  getVehicles,
  updateCoDriver,
  updateDriver,
  updateFoRoute,
  updateVehicle,
} from "../../api/client.js";
import { useSortableData } from "../../hooks/useSortableData.js";
import { ConfirmDialog, Dialog } from "../Dialog.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterRecordFooter,
  DataMasterRowActions,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
} from "./DataMasterLayout.jsx";

const formatter = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 });

function ActiveBadge({ active }) {
  return (
    <span className={`master-active master-active--${active ? "yes" : "no"}`}>
      {active ? "Aktif" : "Nonaktif"}
    </span>
  );
}

function ActiveSelect({ onChange, value }) {
  return (
    <select
      aria-label="Status"
      onChange={(event) => onChange(event.target.value === "active")}
      value={value ? "active" : "inactive"}
    >
      <option value="active">Aktif</option>
      <option value="inactive">Nonaktif</option>
    </select>
  );
}

function ModalActions({ busy, onCancel, submitLabel = "Simpan" }) {
  return (
    <footer className="dialog-card__actions">
      <button className="secondary-button" disabled={busy} onClick={onCancel} type="button">
        Batal
      </button>
      <button className="primary-button" disabled={busy} type="submit">
        {busy ? "Menyimpan..." : submitLabel}
      </button>
    </footer>
  );
}

function AccessStatus({ active, configured }) {
  if (!configured) {
    return <span className="master-active master-active--neutral">Belum dibuat</span>;
  }

  return <ActiveBadge active={active} />;
}

function WorkforceForm({ form, nameLabel, nameKey, onChange }) {
  return (
    <div className="dialog-card__body dialog-form__grid">
      <label>
        <span>{nameLabel}</span>
        <input
          onChange={(event) =>
            onChange((current) => ({ ...current, [nameKey]: event.target.value }))
          }
          required
          value={form[nameKey]}
        />
      </label>
      <label>
        <span>Nomor telepon</span>
        <input
          inputMode="tel"
          onChange={(event) =>
            onChange((current) => ({ ...current, phone: event.target.value }))
          }
          placeholder="Opsional"
          value={form.phone || ""}
        />
      </label>
      <label>
        <span>Status</span>
        <ActiveSelect
          onChange={(active) => onChange((current) => ({ ...current, active }))}
          value={form.active}
        />
      </label>
    </div>
  );
}

function WorkforceMasterTab({
  createRecord,
  deleteRecord,
  description,
  getRecords,
  label,
  nameKey,
  payloadKey,
  responseKey,
  role,
  updateRecord,
}) {
  const empty = { [nameKey]: "", phone: "", active: true };
  const emptyFilters = { search: "", status: "", access: "", assignment: "" };
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getRecords();
      setRows(payload[responseKey] || []);
    } finally {
      setLoading(false);
    }
  }, [getRecords, responseKey]);

  useEffect(() => {
    load().catch((requestError) => setError(requestError.message));
  }, [load]);

  const filteredRows = useMemo(() => {
    const keyword = filters.search.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        keyword &&
        ![row[nameKey], row.phone, row.accessUsername]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword))
      ) {
        return false;
      }

      if (filters.status === "ACTIVE" && !row.active) return false;
      if (filters.status === "INACTIVE" && row.active) return false;
      if (filters.access === "ACTIVE" && !row.accessActive) return false;
      if (filters.access === "INACTIVE" && (!row.accessConfigured || row.accessActive)) return false;
      if (filters.access === "MISSING" && row.accessConfigured) return false;

      const activeAssignmentCount = Number(row.activeFleetOrderCount || 0);
      if (filters.assignment === "ACTIVE" && activeAssignmentCount <= 0) return false;
      if (filters.assignment === "IDLE" && activeAssignmentCount > 0) return false;

      return true;
    });
  }, [filters, nameKey, rows]);

  const summary = useMemo(
    () => ({
      total: filteredRows.length,
      active: filteredRows.filter((row) => row.active).length,
      inactive: filteredRows.filter((row) => !row.active).length,
      access: filteredRows.filter((row) => row.accessConfigured && row.accessActive).length,
      assigned: filteredRows.filter((row) => Number(row.activeFleetOrderCount || 0) > 0).length,
    }),
    [filteredRows],
  );

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, nameKey);

  async function submit(value, id = null) {
    setBusy(true);
    setError("");

    const payload = {
      [payloadKey]: value[nameKey],
      phone: value.phone,
      active: value.active,
    };

    try {
      const response = id
        ? await updateRecord(id, payload)
        : await createRecord(payload);

      setMessage(response.message);
      setForm(empty);
      setCreateOpen(false);
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row) {
    setError("");

    try {
      const response = await updateRecord(row.id, {
        [payloadKey]: row[nameKey],
        phone: row.phone,
        active: !row.active,
      });
      setMessage(response.message);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError("");

    try {
      const response = await deleteRecord(deleteRow.id);
      setMessage(response.message);
      setDeleteRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    setFilters(draftFilters);
  }

  function clearFilters() {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            setForm(empty);
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah {label}
        </button>
      }
      error={error}
      message={message}
      subtitle={description}
      title={label}
    >
      <DataMasterKpiStrip hint="Seluruh hasil filter aktif" label={`Ringkasan ${label.toLowerCase()}`}>
        <DataMasterKpiCard icon="user" label={`Total ${label}`} value={summary.total} />
        <DataMasterKpiCard icon="check" label="Aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="close" label="Nonaktif" value={summary.inactive} />
        <DataMasterKpiCard
          helper="Dapat masuk Sedulur Dalan"
          icon="info"
          label="Akses aktif"
          tone="info"
          value={summary.access}
        />
        <DataMasterKpiCard
          helper="Memiliki Fleet Order terjadwal"
          icon="truck"
          label="Sedang bertugas"
          tone="warning"
          value={summary.assigned}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-workforce-filter-bar" onSubmit={applyFilters}>
        <input
          aria-label={`Cari ${label}`}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, search: event.target.value }))
          }
          placeholder={`Cari nama, telepon, atau username ${label.toLowerCase()}`}
          value={draftFilters.search}
        />
        <select
          aria-label={`Filter status ${label}`}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, status: event.target.value }))
          }
          value={draftFilters.status}
        >
          <option value="">Semua status master</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <select
          aria-label={`Filter akses ${label}`}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, access: event.target.value }))
          }
          value={draftFilters.access}
        >
          <option value="">Semua status akses</option>
          <option value="ACTIVE">Akses aktif</option>
          <option value="INACTIVE">Akses nonaktif</option>
          <option value="MISSING">Belum dibuat</option>
        </select>
        <select
          aria-label={`Filter penugasan ${label}`}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, assignment: event.target.value }))
          }
          value={draftFilters.assignment}
        >
          <option value="">Semua penugasan</option>
          <option value="ACTIVE">Sedang bertugas</option>
          <option value="IDLE">Tidak sedang bertugas</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Status master",
            value: filters.status === "ACTIVE" ? "Aktif" : filters.status === "INACTIVE" ? "Nonaktif" : "",
          },
          {
            label: "Akses",
            value:
              filters.access === "ACTIVE"
                ? "Aktif"
                : filters.access === "INACTIVE"
                  ? "Nonaktif"
                  : filters.access === "MISSING"
                    ? "Belum dibuat"
                    : "",
          },
          {
            label: "Penugasan",
            value: filters.assignment === "ACTIVE" ? "Sedang bertugas" : filters.assignment === "IDLE" ? "Tidak sedang bertugas" : "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--workforce"
        emptyText={`${label} tidak ditemukan.`}
        loading={loading}
        mode="compact"
        rowCount={filteredRows.length}
      >
        <table className="master-table master-table--workforce-compact">
          <thead>
            <tr>
              <SortableHeader column={nameKey} sort={sort} toggleSort={toggleSort}>
                {label}
              </SortableHeader>
              <SortableHeader column="accessUsername" sort={sort} toggleSort={toggleSort}>
                Akses Sedulur Dalan
              </SortableHeader>
              <SortableHeader column="usageCount" sort={sort} toggleSort={toggleSort}>
                Penggunaan
              </SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>
                Status
              </SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row[nameKey]}</strong>
                    <span>{row.phone || `${role} operasional`}</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack dm-access-summary-cell">
                    <AccessStatus active={row.accessActive} configured={row.accessConfigured} />
                    <span>{row.accessUsername || "Username belum dibuat"}</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{formatter.format(Number(row.usageCount || 0))} Fleet Order</strong>
                    <span>
                      {Number(row.activeFleetOrderCount || 0) > 0
                        ? `${formatter.format(Number(row.activeFleetOrderCount || 0))} sedang terjadwal`
                        : "Tidak sedang bertugas"}
                    </span>
                  </div>
                </td>
                <td><ActiveBadge active={row.active} /></td>
                <td className="sticky-action-cell master-action-cell">
                  <DataMasterRowActions
                    active={row.active}
                    onDelete={() => setDeleteRow(row)}
                    onEdit={() => setEditRow({ ...row })}
                    onToggle={() => toggle(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>
      <DataMasterRecordFooter count={filteredRows.length} />

      {createOpen && (
        <Dialog onClose={() => setCreateOpen(false)} title={`Tambah ${label}`}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(form);
            }}
          >
            <WorkforceForm form={form} nameKey={nameKey} nameLabel={`Nama ${label}`} onChange={setForm} />
            <ModalActions busy={busy} onCancel={() => setCreateOpen(false)} submitLabel={`Tambah ${label}`} />
          </form>
        </Dialog>
      )}

      {editRow && (
        <Dialog onClose={() => setEditRow(null)} title={`Edit ${label}`}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(editRow, editRow.id);
            }}
          >
            <WorkforceForm form={editRow} nameKey={nameKey} nameLabel={`Nama ${label}`} onChange={setEditRow} />
            <ModalActions busy={busy} onCancel={() => setEditRow(null)} />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus ${label.toLowerCase()} "${deleteRow[nameKey]}"?`}
          onCancel={() => setDeleteRow(null)}
          onConfirm={confirmDelete}
        />
      )}
    </DataMasterSection>
  );
}

export function DriversTab() {
  return (
    <WorkforceMasterTab
      createRecord={createDriver}
      deleteRecord={deleteDriver}
      description="Kelola driver, status akses Sedulur Dalan, dan riwayat penggunaannya pada Fleet Order."
      getRecords={getDrivers}
      label="Driver"
      nameKey="driverName"
      payloadKey="driverName"
      responseKey="drivers"
      role="Driver"
      updateRecord={updateDriver}
    />
  );
}

export function CoDriversTab() {
  return (
    <WorkforceMasterTab
      createRecord={createCoDriver}
      deleteRecord={deleteCoDriver}
      description="Kelola co-driver, status akses Sedulur Dalan, dan riwayat penggunaannya pada Fleet Order."
      getRecords={getCoDrivers}
      label="Co-Driver"
      nameKey="coDriverName"
      payloadKey="coDriverName"
      responseKey="coDrivers"
      role="Co-driver"
      updateRecord={updateCoDriver}
    />
  );
}

function vehiclePayload(value) {
  return {
    active: value.active,
    capacityKg: value.capacityKg,
    vehiclePlate: value.vehiclePlate,
    vehicleType: value.vehicleType,
    volumeM3: value.volumeM3,
  };
}

function VehicleForm({ form, onChange }) {
  const fields = [
    ["Nopol / Plat", "vehiclePlate", "text"],
    ["Jenis", "vehicleType", "text"],
    ["Kapasitas (kg)", "capacityKg", "number"],
    ["Volume (m³)", "volumeM3", "number"],
  ];

  return (
    <div className="dialog-card__body dialog-form__grid">
      {fields.map(([label, key, type]) => (
        <label key={key}>
          <span>{label}</span>
          <input
            min={type === "number" ? "0" : undefined}
            onChange={(event) => onChange((current) => ({ ...current, [key]: event.target.value }))}
            required
            step={key === "volumeM3" ? "0.01" : undefined}
            type={type}
            value={form[key]}
          />
        </label>
      ))}
      <label>
        <span>Status</span>
        <ActiveSelect onChange={(active) => onChange((current) => ({ ...current, active }))} value={form.active} />
      </label>
    </div>
  );
}

export function VehiclesTab() {
  const empty = {
    active: true,
    capacityKg: "",
    vehiclePlate: "",
    vehicleType: "",
    volumeM3: "",
  };
  const emptyFilters = { search: "", status: "", type: "", usage: "" };
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getVehicles();
      setRows(payload.vehicles || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch((requestError) => setError(requestError.message));
  }, [load]);

  const vehicleTypes = useMemo(
    () => [...new Set(rows.map((row) => row.vehicleType).filter(Boolean))].sort((a, b) => a.localeCompare(b, "id")),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const keyword = filters.search.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        keyword &&
        ![row.vehiclePlate, row.vehicleType]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword))
      ) {
        return false;
      }

      if (filters.status === "ACTIVE" && !row.active) return false;
      if (filters.status === "INACTIVE" && row.active) return false;
      if (filters.type && row.vehicleType !== filters.type) return false;

      const activeAssignmentCount = Number(row.activeFleetOrderCount || 0);
      if (filters.usage === "ACTIVE" && activeAssignmentCount <= 0) return false;
      if (filters.usage === "IDLE" && activeAssignmentCount > 0) return false;
      if (filters.usage === "UNUSED" && Number(row.usageCount || 0) > 0) return false;

      return true;
    });
  }, [filters, rows]);

  const summary = useMemo(
    () => ({
      total: filteredRows.length,
      active: filteredRows.filter((row) => row.active).length,
      inactive: filteredRows.filter((row) => !row.active).length,
      capacity: filteredRows.reduce((sum, row) => sum + Number(row.capacityKg || 0), 0),
      assigned: filteredRows.filter((row) => Number(row.activeFleetOrderCount || 0) > 0).length,
    }),
    [filteredRows],
  );

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, "vehiclePlate");

  async function submit(value, id = null) {
    setBusy(true);
    setError("");

    try {
      const response = id
        ? await updateVehicle(id, vehiclePayload(value))
        : await createVehicle(vehiclePayload(value));
      setMessage(response.message);
      setForm(empty);
      setCreateOpen(false);
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row) {
    setError("");

    try {
      const response = await updateVehicle(row.id, {
        ...row,
        active: !row.active,
      });
      setMessage(response.message);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError("");

    try {
      const response = await deleteVehicle(deleteRow.id);
      setMessage(response.message);
      setDeleteRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    setFilters(draftFilters);
  }

  function clearFilters() {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            setForm(empty);
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah Kendaraan
        </button>
      }
      error={error}
      message={message}
      subtitle="Kelola kapasitas armada dan pantau kendaraan yang sedang digunakan pada Fleet Order terjadwal."
      title="Kendaraan"
    >
      <DataMasterKpiStrip hint="Seluruh hasil filter aktif" label="Ringkasan kendaraan">
        <DataMasterKpiCard icon="truck" label="Total kendaraan" value={summary.total} />
        <DataMasterKpiCard icon="check" label="Aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="close" label="Nonaktif" value={summary.inactive} />
        <DataMasterKpiCard
          helper="Kilogram kapasitas armada"
          icon="database"
          label="Kapasitas total"
          tone="info"
          value={summary.capacity}
        />
        <DataMasterKpiCard
          helper="Dipakai Fleet Order terjadwal"
          icon="route"
          label="Sedang digunakan"
          tone="warning"
          value={summary.assigned}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-workforce-filter-bar" onSubmit={applyFilters}>
        <input
          aria-label="Cari kendaraan"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, search: event.target.value }))
          }
          placeholder="Cari nomor polisi atau jenis kendaraan"
          value={draftFilters.search}
        />
        <select
          aria-label="Filter jenis kendaraan"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, type: event.target.value }))
          }
          value={draftFilters.type}
        >
          <option value="">Semua jenis</option>
          {vehicleTypes.map((vehicleType) => (
            <option key={vehicleType} value={vehicleType}>{vehicleType}</option>
          ))}
        </select>
        <select
          aria-label="Filter status kendaraan"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, status: event.target.value }))
          }
          value={draftFilters.status}
        >
          <option value="">Semua status</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <select
          aria-label="Filter penggunaan kendaraan"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, usage: event.target.value }))
          }
          value={draftFilters.usage}
        >
          <option value="">Semua penggunaan</option>
          <option value="ACTIVE">Sedang digunakan</option>
          <option value="IDLE">Tidak sedang digunakan</option>
          <option value="UNUSED">Belum pernah digunakan</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Jenis", value: filters.type },
          {
            label: "Status",
            value: filters.status === "ACTIVE" ? "Aktif" : filters.status === "INACTIVE" ? "Nonaktif" : "",
          },
          {
            label: "Penggunaan",
            value:
              filters.usage === "ACTIVE"
                ? "Sedang digunakan"
                : filters.usage === "IDLE"
                  ? "Tidak sedang digunakan"
                  : filters.usage === "UNUSED"
                    ? "Belum pernah digunakan"
                    : "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--vehicles"
        emptyText="Kendaraan tidak ditemukan."
        loading={loading}
        mode="compact"
        rowCount={filteredRows.length}
      >
        <table className="master-table master-table--vehicles-compact">
          <thead>
            <tr>
              <SortableHeader column="vehiclePlate" sort={sort} toggleSort={toggleSort}>Kendaraan</SortableHeader>
              <SortableHeader column="capacityKg" sort={sort} toggleSort={toggleSort}>Kapasitas armada</SortableHeader>
              <SortableHeader column="usageCount" sort={sort} toggleSort={toggleSort}>Penggunaan</SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Status</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row.vehiclePlate}</strong>
                    <span>{row.vehicleType || "Jenis belum ditentukan"}</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{formatter.format(Number(row.capacityKg || 0))} kg</strong>
                    <span>{formatter.format(Number(row.volumeM3 || 0))} m³</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{formatter.format(Number(row.usageCount || 0))} Fleet Order</strong>
                    <span>
                      {Number(row.activeFleetOrderCount || 0) > 0
                        ? `${formatter.format(Number(row.activeFleetOrderCount || 0))} sedang terjadwal`
                        : "Tidak sedang digunakan"}
                    </span>
                  </div>
                </td>
                <td><ActiveBadge active={row.active} /></td>
                <td className="sticky-action-cell master-action-cell">
                  <DataMasterRowActions
                    active={row.active}
                    onDelete={() => setDeleteRow(row)}
                    onEdit={() => setEditRow({ ...row })}
                    onToggle={() => toggle(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>
      <DataMasterRecordFooter count={filteredRows.length} />

      {createOpen && (
        <Dialog onClose={() => setCreateOpen(false)} title="Tambah Kendaraan">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(form);
            }}
          >
            <VehicleForm form={form} onChange={setForm} />
            <ModalActions busy={busy} onCancel={() => setCreateOpen(false)} submitLabel="Tambah Kendaraan" />
          </form>
        </Dialog>
      )}

      {editRow && (
        <Dialog onClose={() => setEditRow(null)} title="Edit Kendaraan">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(editRow, editRow.id);
            }}
          >
            <VehicleForm form={editRow} onChange={setEditRow} />
            <ModalActions busy={busy} onCancel={() => setEditRow(null)} />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus kendaraan "${deleteRow.vehiclePlate}"?`}
          onCancel={() => setDeleteRow(null)}
          onConfirm={confirmDelete}
        />
      )}
    </DataMasterSection>
  );
}

function foRoutePayload(value) {
  return {
    active: value.active,
    foRouteCode: value.foRouteCode,
    foRouteName: value.foRouteName,
  };
}

function FoRouteForm({ form, onChange }) {
  return (
    <div className="dialog-card__body dialog-form__grid">
      <label>
        <span>Kode Rute FO</span>
        <input
          onChange={(event) => onChange((current) => ({ ...current, foRouteCode: event.target.value }))}
          required
          value={form.foRouteCode}
        />
      </label>
      <label>
        <span>Nama Rute FO</span>
        <input
          onChange={(event) => onChange((current) => ({ ...current, foRouteName: event.target.value }))}
          required
          value={form.foRouteName}
        />
      </label>
      <label>
        <span>Status</span>
        <ActiveSelect onChange={(active) => onChange((current) => ({ ...current, active }))} value={form.active} />
      </label>
    </div>
  );
}

export function FoRoutesTab() {
  const empty = { active: true, foRouteCode: "", foRouteName: "" };
  const emptyFilters = { search: "", status: "", usage: "" };
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const payload = await getFoRoutes();
    setRows(payload.foRoutes || []);
  }, []);

  useEffect(() => {
    load().catch((requestError) => setError(requestError.message));
  }, [load]);

  const filteredRows = useMemo(() => {
    const keyword = filters.search.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        keyword &&
        ![row.foRouteCode, row.foRouteName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword))
      ) {
        return false;
      }

      if (filters.status === "ACTIVE" && !row.active) return false;
      if (filters.status === "INACTIVE" && row.active) return false;

      const usageCount = Number(row.usageCount || 0);
      if (filters.usage === "USED" && usageCount <= 0) return false;
      if (filters.usage === "UNUSED" && usageCount > 0) return false;

      return true;
    });
  }, [filters, rows]);

  const summary = useMemo(
    () => ({
      total: filteredRows.length,
      active: filteredRows.filter((row) => row.active).length,
      inactive: filteredRows.filter((row) => !row.active).length,
      used: filteredRows.filter((row) => Number(row.usageCount || 0) > 0).length,
    }),
    [filteredRows],
  );

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, "foRouteName");

  async function submit(value, id = null) {
    setBusy(true);
    setError("");

    try {
      const response = id
        ? await updateFoRoute(id, foRoutePayload(value))
        : await createFoRoute(foRoutePayload(value));
      setMessage(response.message);
      setForm(empty);
      setCreateOpen(false);
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row) {
    setError("");

    try {
      const response = await updateFoRoute(row.id, {
        ...row,
        active: !row.active,
      });
      setMessage(response.message);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError("");

    try {
      const response = await deleteFoRoute(deleteRow.id);
      setMessage(response.message);
      setDeleteRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    setFilters(draftFilters);
  }

  function clearFilters() {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            setForm(empty);
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah Rute FO
        </button>
      }
      error={error}
      message={message}
      subtitle="Rute perjalanan Fleet Order, terpisah dari pengelompokan Sales Order."
      title="Rute FO"
    >
      <DataMasterKpiStrip hint="Seluruh hasil filter aktif" label="Ringkasan Rute FO">
        <DataMasterKpiCard icon="route" label="Total Rute FO" value={summary.total} />
        <DataMasterKpiCard icon="check" label="Aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="close" label="Nonaktif" value={summary.inactive} />
        <DataMasterKpiCard
          helper="Pernah dipakai Fleet Order"
          icon="truck"
          label="Pernah digunakan"
          tone="info"
          value={summary.used}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-location-filter-bar dm-location-filter-bar--compact" onSubmit={applyFilters}>
        <input
          aria-label="Cari Rute FO"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, search: event.target.value }))
          }
          placeholder="Cari kode atau nama Rute FO"
          value={draftFilters.search}
        />
        <select
          aria-label="Filter status Rute FO"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, status: event.target.value }))
          }
          value={draftFilters.status}
        >
          <option value="">Semua status</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <select
          aria-label="Filter penggunaan Rute FO"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, usage: event.target.value }))
          }
          value={draftFilters.usage}
        >
          <option value="">Semua penggunaan</option>
          <option value="USED">Sudah digunakan</option>
          <option value="UNUSED">Belum digunakan</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Status",
            value: filters.status === "ACTIVE" ? "Aktif" : filters.status === "INACTIVE" ? "Nonaktif" : "",
          },
          {
            label: "Penggunaan",
            value: filters.usage === "USED" ? "Sudah digunakan" : filters.usage === "UNUSED" ? "Belum digunakan" : "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--fo-routes"
        emptyText="Tidak ada Rute FO yang sesuai filter."
        loading={false}
        mode="compact"
        rowCount={filteredRows.length}
      >
        <table className="master-table master-table--fo-routes-compact">
          <thead>
            <tr>
              <SortableHeader column="foRouteName" sort={sort} toggleSort={toggleSort}>Rute FO</SortableHeader>
              <SortableHeader column="usageCount" sort={sort} toggleSort={toggleSort}>Penggunaan</SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Status</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row.foRouteName}</strong>
                    <span>{row.foRouteCode}</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{Number(row.usageCount || 0).toLocaleString("id-ID")} Fleet Order</strong>
                    <span>{Number(row.usageCount || 0) > 0 ? "Sudah digunakan" : "Belum digunakan"}</span>
                  </div>
                </td>
                <td><ActiveBadge active={row.active} /></td>
                <td className="sticky-action-cell master-action-cell">
                  <DataMasterRowActions
                    active={row.active}
                    onDelete={() => setDeleteRow(row)}
                    onEdit={() => setEditRow({ ...row })}
                    onToggle={() => toggle(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>
      <DataMasterRecordFooter count={filteredRows.length} />

      {createOpen && (
        <Dialog onClose={() => setCreateOpen(false)} title="Tambah Rute FO">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(form);
            }}
          >
            <FoRouteForm form={form} onChange={setForm} />
            <ModalActions busy={busy} onCancel={() => setCreateOpen(false)} submitLabel="Tambah Rute FO" />
          </form>
        </Dialog>
      )}

      {editRow && (
        <Dialog onClose={() => setEditRow(null)} title="Edit Rute FO">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(editRow, editRow.id);
            }}
          >
            <FoRouteForm form={editRow} onChange={setEditRow} />
            <ModalActions busy={busy} onCancel={() => setEditRow(null)} />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus Rute FO "${deleteRow.foRouteName}"?`}
          onCancel={() => setDeleteRow(null)}
          onConfirm={confirmDelete}
        />
      )}
    </DataMasterSection>
  );
}
